import ProductSelection from "../../../domains2/orders/ProductSelection";
import appConfig from "../../../appconfig";
import ShopHelper from "../../../helper/ShopHelper";
import {generateExpression} from "../../../domains2/helpers/targetGroups";
import _ from "lodash";
import LoggerProvider from "../../../providers/LoggerProvider";
import OrderHelperObject from "../../../domains2/orders/OrderHelperObject";
import {extractAppliedSunsetPoints} from "../../../domains2/orders/OrderLineMappingG5Helper";
import {DomainCoreGeneration} from "../../../domains2/libraries/DomainCoreGeneration";
import {evaluateCustomerType} from "../../../libraries/utils";
import {convertProductSelectionsToMap} from "../../../helper/ProductSelectionHelper";

const tenantConfig = appConfig.tenantConfig;

// getters
const CartGetters = {
	customerTrackingTag: state => state.persistedData.customerTrackingTagData,
	estimatedPoints: state => state.estimatedPoints,
	currentCatalogPageExtId: state => state.currentCatalogPageExtId,
	currentProductId: state => state.currentProductId,
	retroactivePointsAssignmentPendingOrderId: state => state.persistedData.retroactivePointsAssignmentPendingOrderId,
	employeeDiscountToken: state => state.persistedData.employeeDiscountToken,
	sunsetPoints: state => state.persistedData.sunsetPoints,
	appliedSunsetPoints: (_, getters) => extractAppliedSunsetPoints(getters.orderLines),
	upsellModalOpenState: state => state.isUpsellModalOpened,
	mysteryBoxModalState: state => state.persistedData.shouldShowMysteryBox,
	isReorderState: state => state.persistedData.isReorder,
	originalProductSelectionsFromPreviousOrder: state => state.persistedData.originalProductSelectionsFromPreviousOrder,
	originalPreviousOrderTotal: state => state.persistedData.originalPreviousOrderTotal,
	showEmployeeDiscountSuccessMessage: state => state.showEmployeeDiscountSuccessMessage,
	recommendedProducts: state => state.recommendedProducts,
	recommendedProductsForCatalogPage: state => state.persistedData.recommendedProductsCatalogPage,
	pickedRecommendedProducts: state => state.pickedRecommendedProducts,
	experimentId: state => state.experimentId,
	orderEditMode: state => state.persistedData.orderEditMode,
	orderEditType: state => state.persistedData.orderEditType,
	customerEvaluationData: state => state.persistedData.customerEvaluationData,
	targetGroupData: state => state.persistedData.targetGroupData,
	vouchersRedeemed: state => state.persistedData.newCartData.vouchersRedeemed,
	voucherVirtualOrderId: state => state.persistedData.newCartData.voucherVirtualOrderId,
	discountCapped: state => state.discountCapped,
	selectedDealId: state => state.persistedData.selectedDealId,
	cartOrderId: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.cartOrderId;
		} else {
			return state.persistedData.newCartData.cartOrderId;
		}
	},
	cartOrderTags: state => {
		const key = state.persistedData.orderEditMode ? "editModeData" : "newCartData";
		return state.persistedData[key].cartOrderTags;
	},
	selections: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.productSelections;
		} else {
			return state.persistedData.newCartData.productSelections;
		}
	},
	cartProducts: (state, getters) => getters.selections,
	orderLines: (state, getters) => {
		if (!getters.productCatalog) {
			return [];
		}

		return ProductSelection.mapToOrderLinesGx(appConfig.getBusinessModelId())(
			getters.productCatalog,
			getters.selections,
			false,
			{
				vouchers: getters.vouchersRedeemed,
				...getters.tenantSpecificData,
				employeeDiscountToken: getters.employeeDiscountToken,
				sunsetPoints: getters.sunsetPoints,
				dailySpending: getters.featureToggles.discountDailyLimit
					? getters.applicationUserData && getters.applicationUserData.meta && getters.applicationUserData.meta.dailySpending
					: {},
				channel: appConfig.channelAlias(),
				deliveryMethod: getters.tenantSpecificData && getters.tenantSpecificData.deliveryChoice,
				customerType: evaluateCustomerType(),
				cartProductsMap: convertProductSelectionsToMap(getters.cartProducts),
				enableProductBestPriceInCatalogAndCart: getters.featureToggles.enableProductBestPriceInCatalogAndCart
			},
			getters.cartOrderTags);
	},
	cartProductsCount: (state, getters) => {
		return OrderHelperObject.getTotalProductSelectionsQuantity(getters.selections);
	},
	mustBeHeated: (state, getters) => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.mustBeHeated
				&& OrderHelperObject.hasHeatableProduct(getters.selections, getters.productCatalog);
		} else {
			return state.persistedData.newCartData.mustBeHeated && tenantConfig.offerHeatedOffers
				&& OrderHelperObject.hasHeatableProduct(getters.selections, getters.productCatalog);
		}
	},
	totalPrice: (state, getters) => {
		return OrderHelperObject.getTotalPrice(
			getters.selections,
			getters.productCatalog,
			{
				options: {
					vouchers: getters.vouchersRedeemed,
					employeeDiscountToken: getters.employeeDiscountToken,
					loyaltySystemDiscount: getters.tenantSpecificData && getters.tenantSpecificData.loyaltySystemDiscount,
					sunsetPoints: getters.sunsetPoints,
					dailySpending: getters.featureToggles.discountDailyLimit
						? getters.applicationUserData && getters.applicationUserData.meta && getters.applicationUserData.meta.dailySpending
						: {},
					channel: appConfig.channelAlias(),
					deliveryMethod: getters.tenantSpecificData.deliveryChoice,
					customerType: evaluateCustomerType(),
					enableProductBestPriceInCatalogAndCart: getters.featureToggles.enableProductBestPriceInCatalogAndCart,
					cartProductsMap: convertProductSelectionsToMap(getters.cartProducts)
				},
				orderTags: getters.cartOrderTags,
				generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId())
			}
		);
	},
	totalPriceWithReturnableItems: (state, getters) => {
		return OrderHelperObject.getTotalPrice(
			getters.selections,
			getters.productCatalog,
			{
				includeReturnableItems: getters.mustBeHeated,
				options: {vouchers: getters.vouchersRedeemed},
				orderTags: getters.cartOrderTags,
				generation: DomainCoreGeneration.getGenerationByBMId(appConfig.getBusinessModelId()),
				channel: appConfig.channelAlias(),
				deliveryMethod: getters.tenantSpecificData.deliveryChoice,
				cartProductsMap: convertProductSelectionsToMap(getters.cartProducts),
				customerType: evaluateCustomerType()
			}
		);
	},
	hasHeatableProduct: (state, getters) => {
		if (state.persistedData.orderEditMode) {
			return OrderHelperObject.hasHeatableProduct(getters.selections, getters.productCatalog);
		} else {
			return tenantConfig.offerHeatedOffers && OrderHelperObject.hasHeatableProduct(state.persistedData.newCartData.productSelections, getters.productCatalog);
		}
	},
	hasHeatableProductInBasket: (state, getters) => {
		return OrderHelperObject.hasHeatableProduct(state.persistedData.newCartData.productSelections, getters.productCatalog);
	},
	isAsapPickup: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.asapPickup;
		} else {
			return state.persistedData.newCartData.asapPickup;
		}
	},
	checkoutSteps: (state, getters) => {
		const newCheckoutFlow = getters.featureToggles.newCheckoutFlow;

		const shopStep = (() => {
			// if shopSelectionEnabled is enabled skip shop selection
			if (tenantConfig.shopSelectionEnabled) {
				return [];
			}

			return {
				name: "stepShop",
				showInStepper: false
			};
		})();

		const checkoutSteps = (() => {
			if (!newCheckoutFlow) {
				return [
					{
						name: "stepCheckoutInit",
						showInStepper: false
					},
					{
						name: "stepUserDataWrapper",
						showInStepper: true
					},
					{
						name: "stepPickupData",
						showInStepper: true
					},
					{
						name: "stepFinishWrapper",
						showInStepper: true
					},
					{
						name: "stepCompleted",
						showInStepper: false
					}
				];
			} else {
				return [
					{
						name: "shoppingCartStepCheckoutInit",
						showInStepper: false
					},
					{
						name: "orderSent",
						showInStepper: true
					},
					{
						name: "awaitingConfirmation",
						showInStepper: true
					},
					{
						name: "orderConfirmation",
						showInStepper: true
					},
					{
						name: "paymentReceipt",
						showInStepper: true
					},
					{
						name: "invoice",
						showInStepper: true
					},
					{
						name: "stepCompleted",
						showInStepper: false
					},
					{
						name: "stepCheckoutInit",
						showInStepper: false
					},
					{
						name: "stepUserDataWrapper",
						showInStepper: false
					},
					{
						name: "stepPickupData",
						showInStepper: false
					}
				];
			}
		})();

		if (tenantConfig.tenantId === 3) {
			checkoutSteps.splice(1, 0, ...shopStep);
		} else {
			checkoutSteps.splice(2, 0, ...shopStep);
		}

		return checkoutSteps;
	},
	getCurrentStep: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.checkoutStep;
		} else {
			return state.persistedData.newCartData.checkoutStep;
			// return state.persistedData.newCartData.checkoutStep === 0 ? 0 : state.persistedData.newCartData.checkoutStep + 1;
		}
	},
	getCurrentStepGt3: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.checkoutStepGt3;
		} else {
			return state.persistedData.newCartData.checkoutStepGt3;
		}
	},
	getShopData: (state, getters) => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.shopData;
		} else {
			// check if there is global selected shop
			if (_.get(state, "persistedData.newCartData.shopData.id")) {
				return state.persistedData.newCartData.shopData;
			} else if (getters.globalSelectedShop) {
				return ShopHelper.createComponentShop(getters.globalSelectedShop);
			} else {
				return state.persistedData.newCartData.shopData;
			}
		}
	},
	cartUserDataSubmitted: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.cartUserDataSubmitted;
		} else {
			return state.persistedData.newCartData.cartUserDataSubmitted;
		}
	},
	getPickupTime: state => state.persistedData.orderEditMode
		? state.persistedData.editModeData.pickupTime
		: state.persistedData.newCartData.pickupTime,
	getPickupDate: state => state.persistedData.orderEditMode
		? state.persistedData.editModeData.pickupDate
		: state.persistedData.newCartData.pickupDate,
	getPickupData: state => {
		const source = state.persistedData.orderEditMode
			? state.persistedData.editModeData
			: state.persistedData.newCartData;
		return {
			pickupTime: source.pickupTime,
			pickupDate: source.pickupDate,
			...source.pickupData
		};
	},
	getOrderSent: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.orderSent;
		} else {
			return state.persistedData.newCartData.orderSent;
		}
	},
	getOrderIsPrepaid: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.orderIsPrepaid;
		} else {
			return state.persistedData.newCartData.orderIsPrepaid;
		}
	},
	lastOrderNumber: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderNumber;
		} else {
			return state.persistedData.newCartData.lastOrderNumber;
		}
	},
	isOrderChanged: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.isOrderChanged;
		} else {
			return state.persistedData.newCartData.isOrderChanged;
		}
	},
	cartCustomerDetails: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.customerDetails;
		} else {
			return state.persistedData.newCartData.customerDetails;
		}
	},
	cartCustomerDetailsDebug: state => {
		return {
			"editModeData.customerDetails" : state.persistedData.editModeData.customerDetails,
			"editModeData.newCartData": state.persistedData.newCartData.customerDetails,
			orderEditMode: state.persistedData.orderEditMode
		};
	},
	lastOrderPrice: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderPrice;
		} else {
			return state.persistedData.newCartData.lastOrderPrice;
		}
	},
	lastOrderPriceWithReturnableItems: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderPriceWithReturnableItems;
		} else {
			return state.persistedData.newCartData.lastOrderPriceWithReturnableItems;
		}
	},
	lastOrderTotalPriceWithoutDiscount: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderTotalPriceWithoutDiscount;
		} else {
			return state.persistedData.newCartData.lastOrderTotalPriceWithoutDiscount;
		}
	},
	lastOrderPreTotalPriceWithReturnableItems: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderPreTotalPriceWithReturnableItems;
		} else {
			return state.persistedData.newCartData.lastOrderPreTotalPriceWithReturnableItems;
		}
	},
	lastOrderProductIds: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderProductIds;
		} else {
			return state.persistedData.newCartData.lastOrderProductIds;
		}
	},
	lastOrderReceiptLines: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderReceiptLines;
		} else {
			return state.persistedData.newCartData.lastOrderReceiptLines;
		}
	},
	productCategory: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.productCategory;
		} else {
			return state.persistedData.newCartData.productCategory;
		}
	},
	lastOrderIsHeated: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderIsHeated;
		} else {
			return state.persistedData.newCartData.lastOrderIsHeated;
		}
	},
	lastOrderTenantSpecificData: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.lastOrderTenantSpecificData;
		} else {
			return state.persistedData.newCartData.lastOrderTenantSpecificData;
		}
	},
	pixelPurchaseFired: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.pixelPurchaseFired;
		} else {
			return state.persistedData.newCartData.pixelPurchaseFired;
		}
	},
	getSpecialNotes: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.specialNotes;
		} else {
			return state.persistedData.newCartData.specialNotes;
		}
	},
	getUserConsent: state => {
		if (state.persistedData.orderEditMode) {
			return false;
		} else {
			return state.persistedData.newCartData.userConsent;
		}
	},
	tenantSpecificData: state => {
		if (state.persistedData.orderEditMode) {
			return state.persistedData.editModeData.tenantSpecificData;
		} else {
			return state.persistedData.newCartData.tenantSpecificData;
		}
	},
	cachedShops: state => state.cachedShops,
	cachedPickupRules: state => state.cachedPickupRules,
	editOrderGetIsOrderChanged: (state, getters) => {
		if (state.persistedData.orderEditOriginalOrder && state.persistedData.editModeData) {
			const isEqualObject = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);
			// check order selection
			const originalOrder = state.persistedData.orderEditOriginalOrder;
			const newData = state.persistedData.editModeData;
			const newUserData = getters.getUserData;

			if (!isEqualObject(originalOrder.productSelections, newData.productSelections)) {
				return true;
			}

			if (originalOrder.orderOptions.mustBeHeated !== newData.mustBeHeated) {
				return true;
			}

			if (originalOrder.specialNotes !== newData.specialNotes) {
				return true;
			}

			if (originalOrder.orderOptions.deliveryChoice !== newData.tenantSpecificData.deliveryChoice) {
				return true;
			}

			// check user data
			const originalUserData = {
				email: originalOrder.customerDetails.email,
				name: originalOrder.customerDetails.name,
				tel: originalOrder.customerDetails.telephone
			};

			if (!isEqualObject(originalUserData, newUserData)) {
				return true;
			}

			// check shop data
			if (originalOrder.shopId !== newData.shopData.id) {
				return true;
			}

			// check pickup data
			const originalPickupData = {
				pickupDate: originalOrder.pickupDate,
				pickupTime: originalOrder.pickupTime
			};

			const newPickupData = {
				pickupDate: newData.pickupDate,
				pickupTime: newData.pickupTime
			};

			if (!isEqualObject(originalPickupData, newPickupData)) {
				return true;
			}
		}

		return false;
	},
	editOrderGetIsOrderDeliveryChanged: (state, getters) => {
		if (!(state.persistedData.orderEditOriginalOrder && state.persistedData.editModeData)) {
			return false;
		}

		const originalOrder = state.persistedData.orderEditOriginalOrder;

		if (originalOrder.orderOptions.deliveryChoice !== 2 || originalOrder.paymentStatus === "postpaid") {
			return false;
		}

		const newTotalPrice = getters.totalPrice;
		const oldTotalPrice = originalOrder.totalPrice;

		return (newTotalPrice > oldTotalPrice) && !originalOrder.orderOptions.paymentLinkThroughEmail;
	},
	discount: state => state.persistedData.discount,
	productIndexes: state => state.persistedData.productIndexes,
	cartLastUpdateTime: state => state.persistedData.lastUpdateTime,
	deliveryDetails: state => state.persistedData.deliveryDetails,
	temporaryAddress: state => state.persistedData.temporaryDeliveryAddress,
	marketingCard: (state, getters) => {
		const evaluationData = state.persistedData.customerEvaluationData;
		const targetGroup = state.persistedData.targetGroupData || [];
		// eslint-disable-next-line no-undef
		const targetID = getUrlParameter("targetGroup");
		const targetSent = targetGroup.find((target) => target.id === targetID);
		if (targetSent) {
			return {
				isPreview: true,
				...targetSent
			};
		}
		const isTargetGroupEnabledInShop = (options, getters) => {
			const allowedShops = options && options.shops;
			if(allowedShops && allowedShops.length && getters.globalSelectedShop) {
				return allowedShops.includes(getters.globalSelectedShop.id);
			}
			return true;
		};
		const isTargetGroupEnabledInCatalogPage = (options, getters) => {
			const allowedCatalogPages = options && options.catalogPages;
			const currentCatalogPage = getters.currentCatalogPageExtId;
			if (allowedCatalogPages && allowedCatalogPages.length && currentCatalogPage) {
				return allowedCatalogPages.includes(currentCatalogPage);
			}
			return true;
		};
		const isTargetGroupEnabledForProduct = (options, getters) => {
			const allowedProducts = options && options.products;
			const currentProduct = getters.currentProductId;
			if (allowedProducts && allowedProducts.length) {
				if(!currentProduct) {
					return false;
				}
				return allowedProducts.includes(currentProduct);
			}
			return true;
		};
		const mapTargetGroupApplicable = (targetGroups, isAddHoc) => targetGroups
			.filter(({name}) => ["Ad Hoc Target Group"].includes(name) === Boolean(isAddHoc))
			.filter(({options}) => {
				return isTargetGroupEnabledInShop(options, getters)
					&& isTargetGroupEnabledInCatalogPage(options, getters)
					&& isTargetGroupEnabledForProduct(options, getters);
			}).filter((targetGroup) => targetGroup.expressions
				.some((expression) => {
					try {
						return generateExpression(evaluationData, expression);
					} catch (error) {
						LoggerProvider.logEvent({
							message: `|TARGETING GROUPS| *Frontend*: Expression validation failed`,
							eventName: "TargetingGroupExpressionFailed",
							eventCategory: "TargetingGroups",
							eventDomain: "TargetingGroups",
							data: {
								message: error.message,
								name: error.name
							}
						});
						return false;
					}
				})
			);

		const AddHoc = mapTargetGroupApplicable(targetGroup, true)
			.map((target) => target.options.grid)
			.flat();

		const result = mapTargetGroupApplicable(targetGroup, false)
			.sort((a, b) => b.priority - a.priority)
			.map((card) => ({
				...card,
				options: {
					...card.options,
					grid: AddHoc.concat(card.options.grid)
				}
			}));
		return !evaluationData.currentPage ?
			result.find(Boolean) : result.find(x=> x.expressions.some(e => e.toLowerCase().includes(evaluationData.currentPage && evaluationData.currentPage.toLowerCase())));
	},
	groupOrderId: state => state.persistedData.newCartData.groupOrderId,
	isUserGroupOrderOwner: state => state.persistedData.newCartData.isUserGroupOrderOwner,
	groupOrderFriendsOrderData: state => state.persistedData.newCartData.groupOrderFriendsOrderData,

	qrCodeTableServingData: state => state.persistedData.newCartData.qrCodeTableServingData,
	friendInviteCode: state => state.persistedData.friendInviteCode,

	voucherReward: state => state.persistedData.voucherReward,
	userCrmVouchers: state => state.persistedData.userCrmVouchers || [],
	menuProductIdsForReselection: state => state.persistedData.menuProductIdsForReselection,
	unavailableProductIds: state => state.persistedData.unavailableProductIds,
	singleMainProductIdsToMenuIdsMap: state => state.persistedData.singleMainProductIdsToMenuIdsMap,
	upgradeToMenuFromBasketFlag: state => state.persistedData.upgradeToMenuFromBasketFlag,
	productSelectionIdToRemove: state => state.persistedData.productSelectionIdToRemove,
	marketingCardDataOnClick: state => state.marketingCardDataOnClick
};

export default CartGetters;
